import React from "react";
import { Flame, Droplets, Wallet, ExternalLink } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { CopyButton } from "./CopyButton";
import { shortAddr, formatPct } from "../lib/utils";
import { cn } from "../lib/utils";

function LabelBadge({ label }) {
  if (label === "liquidity_pool")
    return (
      <Badge variant="primary" className="gap-1">
        <Droplets size={11} /> LP
      </Badge>
    );
  if (label === "burn")
    return (
      <Badge variant="warning" className="gap-1">
        <Flame size={11} /> Burn
      </Badge>
    );
  if (label === "wallet")
    return (
      <Badge variant="muted" className="gap-1">
        <Wallet size={11} /> Wallet
      </Badge>
    );
  return <Badge variant="muted">Unknown</Badge>;
}

export function HoldersTable({ holders = [], topPct }) {
  if (!holders.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Holders</CardTitle>
          <CardDescription>
            On-chain top-holders unavailable for this token (RPC overloaded for high-supply tokens).
            {typeof topPct === "number" ? (
              <>
                {" "}Aggregate top-holder concentration:{" "}
                <span className="font-mono text-[hsl(var(--fg))]">{topPct.toFixed(2)}%</span>
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card data-testid="holders-table">
      <CardHeader>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Top {holders.length} Holders</CardTitle>
            <CardDescription>
              Sorted by token amount. Concentration of top 10:{" "}
              <span className="font-mono text-[hsl(var(--fg))]">
                {typeof topPct === "number" ? formatPct(topPct) : "—"}
              </span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 sm:px-2">
        {/* Desktop table */}
        <div className="hidden sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[hsl(var(--muted))]">
                <th className="px-3 py-2 w-10">#</th>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">% Supply</th>
                <th className="px-3 py-2">Label</th>
              </tr>
            </thead>
            <tbody>
              {holders.map((h, i) => (
                <tr
                  key={h.address}
                  className={cn(
                    "border-t border-[hsl(var(--border))]",
                    i % 2 === 0 ? "bg-[hsl(var(--bg-elev-1)/0.4)]" : ""
                  )}
                >
                  <td className="px-3 py-2 text-[hsl(var(--muted))] tabular-nums">{i + 1}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <div className="flex items-center gap-1">
                      <a
                        href={`https://solscan.io/account/${h.address}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-[hsl(var(--primary))]"
                        title={h.address}
                      >
                        {shortAddr(h.address, 6, 6)}
                      </a>
                      <CopyButton
                        value={h.address}
                        testId="holders-row-copy-button"
                        label="Copy account"
                      />
                      <a
                        href={`https://solscan.io/account/${h.address}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[hsl(var(--muted))] hover:text-[hsl(var(--primary))]"
                      >
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {h.owner ? (
                      <a
                        href={`https://solscan.io/account/${h.owner}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-[hsl(var(--primary))]"
                        title={h.owner}
                      >
                        {shortAddr(h.owner, 6, 6)}
                      </a>
                    ) : (
                      <span className="text-[hsl(var(--muted))]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                    {h.ui_amount_string ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatPct(h.pct, 3)}
                  </td>
                  <td className="px-3 py-2"><LabelBadge label={h.label} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-[hsl(var(--border))]">
          {holders.map((h, i) => (
            <div key={h.address} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[hsl(var(--muted))]">#{i + 1}</span>
                  <span className="font-mono text-xs">{shortAddr(h.address, 5, 5)}</span>
                  <CopyButton value={h.address} testId="holders-row-copy-button" />
                </div>
                <LabelBadge label={h.label} />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs">
                <span className="text-[hsl(var(--muted))]">Owner</span>
                <span className="font-mono">{h.owner ? shortAddr(h.owner, 5, 5) : "—"}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-[hsl(var(--muted))]">% supply</span>
                <span className="font-mono tabular-nums">{formatPct(h.pct, 3)}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
